import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'
import { useNavigate } from 'react-router-dom'
import { Order } from '../../types'
import { getUnreadCount } from '../../lib/chatService'

const AdminOrdersPage: React.FC = () => {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 🔍 Recherche et filtres
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'ALL' | string>('ALL')
  const [filterPayment, setFilterPayment] = useState<'ALL' | string>('ALL')
  const [sendingFeedback, setSendingFeedback] = useState<Set<string>>(new Set())

  // Charger les commandes
  const loadOrders = async () => {
    setLoading(true)
    setError(null)

    const { data, error } = await supabase
      .from('orders')
      .select(`
        id,
        created_at,
        total_price,
        status,
        payment_status,
        racket_brand,
        racket_description,
        service: services!fk_orders_service (description, name),
        profile: profiles!fk_orders_user (full_name, email),
        messages: messages (created_at)
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error(error)
      setError('Erreur lors du chargement des commandes.')
      setOrders([])
      setLoading(false)
      return
    }

    const ordersBase = (data ?? []).map((o: any) => ({
      id: o.id,
      created_at: o.created_at,
      total_price: o.total_price,
      status: o.status,
      payment_status: o.payment_status,
      racket_brand: o.racket_brand,
      racket_description: o.racket_description,
      service_name: o.service?.name ?? '',
      user_email: o.profile?.full_name ?? o.profile?.email ?? '',
      has_messages: o.messages.length > 0,
      last_message_at:
        o.messages.length > 0
          ? o.messages[o.messages.length - 1].created_at
          : null,
    }))

    // 🟢 calcul des messages non lus (ADMIN)
    const orderIds = ordersBase.map(o => o.id)
    const unreadMap = await getUnreadCount(orderIds, 'ADMIN')

    const ordersWithUnread = ordersBase.map(o => ({
      ...o,
      unread_count: unreadMap[o.id] ?? 0,
    }))

    setOrders(ordersWithUnread)
    setLoading(false)
  }


  useEffect(() => {
    loadOrders()
  }, [])

  const handleSendFeedbackEmail = async (orderId: string) => {
    setSendingFeedback(prev => new Set(prev).add(orderId))

    const enabledEnv = (import.meta.env.VITE_BOOKING_EMAILS_ENABLED ?? 'true') as string
    const sendEnabled = enabledEnv.toLowerCase() !== 'false'

    const { data, error } = await supabase.functions.invoke('send-feedback-email', {
      body: { orderId, sendEnabled },
    })

    setSendingFeedback(prev => {
      const next = new Set(prev)
      next.delete(orderId)
      return next
    })

    if (error || data?.error) {
      alert("Erreur lors de l'envoi du mail de feedback.")
    } else {
      alert('Email de demande de feedback envoyé ✅')
    }
  }

  const handleMarkPaid = async (orderId: string) => {
    if (!window.confirm('Confirmer le marquage de cette commande comme payée ?')) return

    const { error } = await supabase
      .from('orders')
      .update({ payment_status: 'PAID' })
      .eq('id', orderId)

    if (error) {
      console.error(error)
      alert('Erreur lors de la mise à jour du statut de paiement.')
    } else {
      alert('Commande marquée comme payée ✅')
      await loadOrders()
    }
  }

  // --- Filtres / recherche
  const filteredOrders = orders.filter((o) => {
    const matchesSearch =
      searchTerm.trim() === '' ||
      o.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.service_name?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus =
      filterStatus === 'ALL' ? true : o.status === filterStatus

    const matchesPayment =
      filterPayment === 'ALL' ? true : o.payment_status === filterPayment

    return matchesSearch && matchesStatus && matchesPayment
  })

  const statusMessage = loading
    ? 'Chargement des commandes...'
    : error

  return (
    <div className="card">
      <div className="card-header">
        <h1 className="card-title">Gestion des commandes</h1>
        {statusMessage && (
          <p style={{ color: error ? '#f87171' : '#9ca3af', marginTop: '.25rem' }}>{statusMessage}</p>
        )}
      </div>

      {/* --- Filtres et recherche --- */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1rem',
          alignItems: 'center',
        }}
      >
        <input
          data-testid="admin-orders-search"
          type="text"
          placeholder="Rechercher (email ou service)"
          className="input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: '1 1 200px' }}
        />

        <select
          data-testid="admin-orders-filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="input"
          style={{ flex: '1 1 160px' }}
        >
          <option value="ALL">Tous les statuts</option>
          <option value="EN_ATTENTE">EN_ATTENTE</option>
          <option value="EN_COURS">EN_COURS</option>
          <option value="PRET_A_RECUPERER">PRET_A_RECUPERER</option>
          <option value="EN_LIVRAISON">EN_LIVRAISON</option>
          <option value="LIVREE">LIVREE</option>
          <option value="ANNULEE">ANNULEE</option>
        </select>

        <select
          data-testid="admin-orders-filter-payment"
          value={filterPayment}
          onChange={(e) => setFilterPayment(e.target.value)}
          className="input"
          style={{ flex: '1 1 160px' }}
        >
          <option value="ALL">Tous paiements</option>
          <option value="OFFLINE_PENDING">En attente</option>
          <option value="PAID">Payé</option>
        </select>

        <button className="btn btn-ghost" data-testid="admin-orders-refresh" onClick={loadOrders} style={{ flex: '1 1 160px' }}>
          🔄 Rafraîchir
        </button>
      </div>

      {/* --- Tableau des commandes --- */}
      <div className="admin-orders-table-wrapper">
      <table
        className="admin-orders-table"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '.9rem',
        }}
      >
        <thead style={{ background: '#0f172a', color: '#f8fafc' }}>
        <tr>
          <th style={{ padding: '.5rem' }}>#</th>
          <th style={{ padding: '.5rem' }}>Date</th>
          <th style={{ padding: '.5rem' }}>Client</th>
          <th style={{ padding: '.5rem' }}>Service</th>
          <th style={{ padding: '.5rem' }}>Raquette</th>
          <th style={{ padding: '.5rem' }}>Total</th>
          <th style={{ padding: '.5rem' }}>Paiement</th>
          <th style={{ padding: '.5rem' }}>Statut</th>
          <th style={{ padding: '.5rem' }}>Messages</th>
          <th style={{ padding: '.5rem' }}>Action</th>
        </tr>
        </thead>
        <tbody>
        {filteredOrders.map((o, index) => (
          <tr
            key={o.id}
            data-testid={`admin-order-row-${o.id}`}
            onClick={() => navigate(`/admin/orders/${o.id}`)}
            style={{
              borderBottom: '1px solid rgba(148,163,184,0.2)',
              background:
                o.has_messages
                  ? 'rgba(34,197,94,0.04)'
                  : o.payment_status === 'OFFLINE_PENDING'
                    ? 'rgba(255,255,0,0.05)'
                    : 'transparent',
              cursor: 'pointer',
            }}
          >
            <td data-label="#" style={{ padding: '.5rem' }}>{index + 1}</td>
            <td data-label="Date" style={{ padding: '.5rem' }}>
              {new Date(o.created_at).toLocaleDateString('fr-CH')}
            </td>
            <td data-label="Client" style={{ padding: '.5rem' }}>{o.user_email}</td>
            <td data-label="Service" style={{ padding: '.5rem' }}>{o.service_name}</td>
            <td data-label="Raquette" style={{ padding: '.5rem' }}>
              {o.racket_brand}
              {o.racket_description && (
                <span style={{ color: '#94a3b8' }}> — {o.racket_description}</span>
              )}
            </td>
            <td data-label="Total" style={{ padding: '.5rem' }}>{o.total_price.toFixed(2)} CHF</td>
            <td data-label="Paiement" style={{ padding: '.5rem' }}>
              {o.payment_status === 'PAID' ? (
                <span style={{ color: '#22c55e', fontWeight: 600 }}>Payé</span>
              ) : (
                <span style={{ color: '#facc15', fontWeight: 600 }}>En attente</span>
              )}
            </td>
            <td data-label="Statut" style={{ padding: '.5rem' }}>{o.status}</td>
            <td data-label="Messages" style={{ padding: '.5rem', textAlign: 'center' }}>
              {o.has_messages ? (
                <span title={`Dernier message : ${new Date(o.last_message_at!).toLocaleString('fr-CH')}`}>
                  💬
                </span>
              ) : (
                <span style={{ color: '#64748b' }}>—</span>
              )}
              {(o.unread_count ?? 0) > 0 && (
              <span
                style={{
                  background: '#22c55e',
                  color: '#020617',
                  fontSize: '.7rem',
                  padding: '0.15rem 0.4rem',
                  borderRadius: '999px',
                  marginLeft: '.25rem',
                }}
              >
                {o.unread_count}
              </span>
            )}

            </td>
            <td data-label="Action" style={{ padding: '.5rem', display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
              {o.payment_status === 'OFFLINE_PENDING' && (
                <button
                  data-testid={`admin-order-markpaid-${o.id}`}
                  className="btn btn-primary"
                  style={{ fontSize: '.8rem', padding: '.25rem .5rem', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleMarkPaid(o.id)
                  }}
                >
                  Marquer Payé
                </button>
              )}
              {o.status === 'LIVREE' && (
                <button
                  data-testid={`admin-order-feedback-${o.id}`}
                  className="btn btn-ghost"
                  style={{ fontSize: '.8rem', padding: '.25rem .5rem', cursor: 'pointer' }}
                  disabled={sendingFeedback.has(o.id)}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSendFeedbackEmail(o.id)
                  }}
                >
                  {sendingFeedback.has(o.id) ? '⏳ Envoi…' : '⭐ Demander feedback'}
                </button>
              )}
            </td>
          </tr>
        ))}
        </tbody>
      </table>
      </div>

      {filteredOrders.length === 0 && (
        <p style={{ color: '#9ca3af', marginTop: '1rem' }}>
          Aucune commande ne correspond à ta recherche.
        </p>
      )}
    </div>
  )
}

export default AdminOrdersPage
